/**
 * Talks to the Tafo server. Every request goes through `request`, which adds the device token (and,
 * for an action confirmed with a personal code, that authorization), turns error responses into
 * `ApiError`, and retries what is safe to retry.
 */
import { t } from "../i18n";

export const API_BASE = "/api/v1";

/** Waits between attempts when the server is briefly unreachable (network, 503). */
const RETRY_DELAYS_MS = [400, 1_000, 2_500, 5_000];
const REQUEST_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The server could not be reached or asked to try again later; the same request may succeed. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 503;
  }
}

export type Auth = "none" | "device";

export interface RequestOptions {
  body?: unknown;
  /** Default "device": the device token. */
  auth?: Auth;
  /**
   * The authorization of the person who entered their code for this action (a discount, a cancel …).
   * Without it the till itself is the one who acts, which is enough for ordering and taking payments.
   */
  staffToken?: string | undefined;
  /** Extra headers, e.g. the special admin code that goes with a delete. */
  headers?: Record<string, string>;
  /** Required for creating orders and payments: the same key on every attempt of one action. */
  idempotencyKey?: string;
  /** Retry on network errors and 503. Only for requests that are safe to repeat. */
  retry?: boolean;
  signal?: AbortSignal;
}

export type ConnectionState = "online" | "trouble";

export interface Credentials {
  deviceToken(): string | null;
  /**
   * The Tafo app (ADR 0011): the person signed in on this device, sent with every request unless the
   * request carries another person's code (a manager's, to set the device up again).
   */
  staffToken?(): string | null;
}

/** A file from the server, e.g. the quarterly overview for the accountant. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export interface ApiClient {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>;
  /** A file (GET), with the same headers and errors as `request`. */
  download(path: string, options?: RequestOptions): Promise<DownloadedFile>;
  onConnectionChange(listener: (state: ConnectionState) => void): () => void;
  /**
   * Who to ask when the session of a person's code ended in the middle of his work (Beheer, after 30
   * minutes): the handler asks the code again and gives the new session, or null to leave the refusal as
   * it is. The request that was refused is then sent once more, so nobody starts over (Furkan,
   * 05/10/2026). null takes the handler away.
   */
  onCodeExpired(handler: ((staffToken: string) => Promise<string | null>) | null): void;
}

/**
 * Aborted as soon as one of the two is: AbortSignal.any where the browser has it (Chrome 116 and later),
 * otherwise by hand. A shop on an old computer works in Chrome 109, the last one for Windows 7 (Pita
 * Antalya, 08/10/2026: "AbortSignal.any is not a function", the ticket stayed at € 0,00).
 */
export function eitherSignal(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === "function") return AbortSignal.any([a, b]);
  const controller = new AbortController();
  const done = [a, b].find((signal) => signal.aborted);
  if (done) {
    controller.abort(done.reason);
    return controller.signal;
  }
  const stop = (event: Event) => {
    a.removeEventListener("abort", stop);
    b.removeEventListener("abort", stop);
    controller.abort((event.target as AbortSignal).reason);
  };
  a.addEventListener("abort", stop);
  b.addEventListener("abort", stop);
  return controller.signal;
}

export function createApiClient(credentials: Credentials, fetchImpl: typeof fetch = fetch.bind(window)): ApiClient {
  const listeners = new Set<(state: ConnectionState) => void>();
  let state: ConnectionState = "online";
  const setState = (next: ConnectionState) => {
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener(next);
  };

  /** One attempt: the response when the server said yes, otherwise an ApiError. */
  async function send(method: string, path: string, options: RequestOptions, accept: string): Promise<Response> {
    const headers: Record<string, string> = { ...options.headers, accept };
    const auth = options.auth ?? "device";
    if (auth !== "none") {
      const device = credentials.deviceToken();
      if (device !== null) headers["x-device-token"] = device;
    }
    const staffToken = options.staffToken ?? (auth === "none" ? null : (credentials.staffToken?.() ?? null));
    if (staffToken !== null) headers.authorization = `Bearer ${staffToken}`;
    if (options.body !== undefined) headers["content-type"] = "application/json";
    if (options.idempotencyKey !== undefined) headers["idempotency-key"] = options.idempotencyKey;

    const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
    const signal = options.signal ? eitherSignal(options.signal, timeout) : timeout;
    let response: Response;
    try {
      response = await fetchImpl(API_BASE + path, {
        method,
        headers,
        body: options.body === undefined ? null : JSON.stringify(options.body),
        signal,
      });
    } catch (error) {
      if (options.signal?.aborted) throw error; // cancelled by the caller: not a connection problem
      throw new ApiError(0, "network", t.errors.network);
    }
    if (response.ok) return response;

    let body: unknown = null;
    try {
      const text = await response.text();
      body = text === "" ? null : JSON.parse(text);
    } catch {
      // a proxy error page or similar: handled below by the status
    }
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? `http_${String(response.status)}`,
      error?.message ?? (response.status >= 500 ? t.errors.server : t.errors.unexpected),
    );
  }

  async function once<T>(method: string, path: string, options: RequestOptions): Promise<T> {
    const response = await send(method, path, options, "application/json");
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    try {
      return (text === "" ? null : JSON.parse(text)) as T;
    } catch {
      return null as T; // not JSON (as before): the caller sees no body
    }
  }

  /** Retries what is safe to retry, and keeps the connection state. */
  async function attempt<T>(work: () => Promise<T>, options: RequestOptions): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        const result = await work();
        setState("online");
        return result;
      } catch (error) {
        if (!(error instanceof ApiError) || !error.retryable) {
          if (error instanceof ApiError) setState("online"); // the server answered
          throw error;
        }
        setState("trouble");
        const delay = RETRY_DELAYS_MS[attempt];
        if (options.retry !== true || delay === undefined || options.signal?.aborted) throw error;
        await sleep(delay, options.signal);
      }
    }
  }

  let askCode: ((staffToken: string) => Promise<string | null>) | null = null;
  /** The code being asked again, per session that ended: every request refused meanwhile waits for the same answer. */
  const asking = new Map<string, Promise<string | null>>();
  /** Sessions that ended and the session that took their place, for a request still under way with the old one. */
  const renewed = new Map<string, string>();

  /** Runs the work; when the session of the code ended, asks the code again and runs it once more. */
  async function withCode<T>(options: RequestOptions, work: (options: RequestOptions) => Promise<T>): Promise<T> {
    try {
      return await work(options);
    } catch (error) {
      const ended = options.staffToken;
      if (!(error instanceof ApiError) || error.code !== "session_expired" || ended === undefined) throw error;
      let fresh: string | null = renewed.get(ended) ?? null;
      if (fresh === null && askCode !== null) {
        let answer = asking.get(ended);
        if (answer === undefined) {
          answer = askCode(ended).finally(() => asking.delete(ended));
          asking.set(ended, answer);
        }
        fresh = await answer;
        if (fresh !== null) {
          if (renewed.size >= 20) renewed.clear();
          renewed.set(ended, fresh);
        }
      }
      if (fresh === null) throw error;
      return work({ ...options, staffToken: fresh });
    }
  }

  return {
    request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
      return withCode(options, (sent) => attempt(() => once<T>(method, path, sent), sent));
    },
    onCodeExpired(handler) {
      askCode = handler;
    },
    download(path: string, given: RequestOptions = {}): Promise<DownloadedFile> {
      return withCode(given, (options) =>
        attempt(async () => {
          const response = await send("GET", path, options, "*/*");
          const disposition = response.headers.get("content-disposition") ?? "";
          // filename*=UTF-8''… (names with accents, "€" or brackets) or the plain filename="…"
          const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
          const name =
            (encoded === undefined ? undefined : safeDecode(encoded)) ??
            /filename="([^"]+)"/.exec(disposition)?.[1] ??
            path.split("/").pop() ??
            "download";
          return { blob: await response.blob(), filename: name };
        }, options),
      );
    },
    onConnectionChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function safeDecode(text: string): string | undefined {
  try {
    return decodeURIComponent(text);
  } catch {
    return undefined;
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

/** A new key for one user action (a sale, a payment). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
